import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

const SpotifyIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden><path d="M12 0a12 12 0 1 0 0 24 12 12 0 0 0 0-24zm5.5 17.3a.75.75 0 0 1-1.03.25c-2.82-1.72-6.37-2.11-10.55-1.16a.75.75 0 1 1-.33-1.46c4.57-1.05 8.5-.6 11.66 1.33.35.22.46.68.25 1.04zm1.47-3.27a.94.94 0 0 1-1.29.31c-3.23-1.98-8.15-2.56-11.97-1.4a.94.94 0 1 1-.54-1.8c4.36-1.32 9.79-.68 13.5 1.6.44.27.58.85.3 1.29zm.13-3.4C15.24 8.33 8.9 8.12 5.2 9.24a1.13 1.13 0 1 1-.65-2.16c4.24-1.29 11.3-1.04 15.75 1.6a1.13 1.13 0 0 1-1.15 1.94z" /></svg>
);

const cache = new Map();
const showSearch = (episode) => `https://open.spotify.com/search/${encodeURIComponent((episode.show?.name || episode.title || "").slice(0, 150))}/podcastAndEpisodes`;

// Links to the podcast's main Spotify page (not the individual episode); resolved once per show and cached
export const SpotifyButton = ({ episode, size = "md", testId = "spotify-button" }) => {
  const slug = episode.show?.slug || "";
  const [link, setLink] = useState(() => cache.get(slug) || { url: episode.show?.spotify_url || showSearch(episode), exact: !!episode.show?.spotify_url });
  const seen = useRef(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!slug || link.exact || cache.has(slug) || episode.video_id) return;
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting || seen.current) return;
      seen.current = true;
      api.get(`/shows/${slug}/spotify`).then((r) => {
        const next = { url: r.data.spotify_url || r.data.fallback_url, exact: r.data.exact };
        cache.set(slug, next); setLink(next);
      }).catch(() => {});
    }, { rootMargin: "200px" });
    io.observe(el);
    return () => io.disconnect();
  }, [slug, episode.video_id, link.exact]);

  if (episode.video_id && !episode.audio_url) return null;
  const dims = size === "lg" ? "h-14 px-5 gap-2 text-sm" : "h-9 px-3 gap-1.5 text-xs";
  return (
    <a ref={ref} href={link.url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} title={link.exact ? `${episode.show?.name || "This show"} on Spotify` : "Find this show on Spotify"} className={`${dims} inline-flex items-center rounded-full border border-[#1DB954]/40 bg-[#1DB954]/10 font-semibold text-[#1ed760] hover:bg-[#1DB954] hover:text-[#071a0e] transition-colors`} aria-label="Open this show on Spotify" data-testid={testId} data-exact={link.exact}>
      <SpotifyIcon size={size === "lg" ? 18 : 14} /> Spotify
    </a>
  );
};
